import { useEffect, useState } from "react";

import TestPanel from "../components/TestPanel";
import { api } from "../lib/api";
import type { Lane, Watch } from "../lib/types";

interface Draft {
  name: string;
  criteria: string;
  extract_schema: Record<string, unknown> | null;
  lane_id: number | null;
  max_price: number | null;
  min_confidence: number;
  notify: boolean;
  notify_priority: "low" | "normal" | "high";
  enabled: boolean;
}

const BLANK: Draft = {
  name: "",
  criteria: "",
  extract_schema: null,
  lane_id: null,
  max_price: null,
  min_confidence: 0.6,
  notify: true,
  notify_priority: "normal",
  enabled: true,
};

/** The watch editor. Criteria is the primary instrument; everything else is a guard rail. */
export default function Watches() {
  const [watches, setWatches] = useState<Watch[]>([]);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [selected, setSelected] = useState<Watch | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [schemaText, setSchemaText] = useState("");
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [w, l] = await Promise.all([api.watches(), api.lanes()]);
      setWatches(w);
      setLanes(l);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  function edit(watch: Watch | null) {
    setSelected(watch);
    setError(null);
    setSchemaError(null);
    if (watch) {
      setDraft({
        name: watch.name,
        criteria: watch.criteria,
        extract_schema: watch.extract_schema,
        lane_id: watch.lane_id,
        max_price: watch.max_price,
        min_confidence: watch.min_confidence,
        notify: watch.notify,
        notify_priority: watch.notify_priority,
        enabled: watch.enabled,
      });
      setSchemaText(watch.extract_schema ? JSON.stringify(watch.extract_schema, null, 2) : "");
    } else {
      setDraft(BLANK);
      setSchemaText("");
    }
  }

  function parseSchema(text: string): Record<string, unknown> | null | undefined {
    if (!text.trim()) return null;
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("must be a JSON object of property -> schema");
      }
      setSchemaError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setSchemaError(err instanceof Error ? err.message : String(err));
      return undefined;
    }
  }

  async function save() {
    const schema = parseSchema(schemaText);
    if (schema === undefined) return;
    setSaving(true);
    setError(null);
    try {
      const body = { ...draft, extract_schema: schema };
      const saved = selected
        ? await api.patchWatch(selected.id, body)
        : await api.createWatch(body);
      await load();
      edit(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function toggle(watch: Watch) {
    const updated = await api.patchWatch(watch.id, { enabled: !watch.enabled });
    setWatches((prev) => prev.map((w) => (w.id === watch.id ? updated : w)));
    if (selected?.id === watch.id) setSelected(updated);
  }

  async function rescore(watch: Watch, scope: "rejects" | "all", rerunStage0: boolean) {
    const what =
      scope === "rejects"
        ? "everything this watch turned down (existing matches stay in the feed)"
        : "every verdict for this watch";
    if (
      !confirm(
        `Re-rate ${what} from the last 30 days against the current criteria?` +
          (scope === "all"
            ? "\n\nCurrent matches leave the feed now, and return only if the model confirms " +
              "them again."
            : "") +
          (rerunStage0 ? "\n\nStage 0 decisions will also be re-run." : "") +
          "\n\nRe-confirmed matches will not push again. Flagged misses are kept.",
      )
    )
      return;
    try {
      const result = await api.rescoreWatch(watch.id, {
        scope,
        days: 30,
        rerun_stage0: rerunStage0,
        lane_id: watch.lane_id,
      });
      setNotice(result.note + ` ${result.pending} listing(s) queued.`);
      window.setTimeout(() => setNotice(null), 20000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function remove(watch: Watch) {
    if (!confirm(`Delete "${watch.name}" and all of its verdicts?`)) return;
    await api.deleteWatch(watch.id);
    if (selected?.id === watch.id) edit(null);
    await load();
  }

  return (
    <div className="grid-2">
      <div className="stack">
        <div className="row">
          <h2 style={{ fontSize: 15 }}>Watches</h2>
          <div className="spacer" />
          <button onClick={() => edit(null)}>New watch</button>
        </div>

        {!watches.length && <div className="empty">No watches yet.</div>}

        {watches.map((watch) => (
          <div
            key={watch.id}
            className="card"
            style={{
              borderColor: selected?.id === watch.id ? "var(--accent)" : undefined,
              cursor: "pointer",
            }}
            onClick={() => edit(watch)}
          >
            <div className="row" style={{ justifyContent: "space-between" }}>
              <strong>{watch.name}</strong>
              <span className={`pill ${watch.enabled ? "pass" : ""}`}>
                {watch.enabled ? "enabled" : "paused"}
              </span>
            </div>
            <p className="faint" style={{ margin: "6px 0 0" }}>
              {watch.criteria.length > 150 ? `${watch.criteria.slice(0, 150)}...` : watch.criteria}
            </p>
            <div className="row" style={{ marginTop: 8 }}>
              <span className="pill">min conf {watch.min_confidence}</span>
              {watch.max_price !== null && <span className="pill">max {watch.max_price}</span>}
              <span className="pill">
                {watch.lane_id
                  ? (lanes.find((l) => l.id === watch.lane_id)?.name ?? `lane ${watch.lane_id}`)
                  : "all lanes"}
              </span>
              <div className="spacer" />
              <button
                className="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  void toggle(watch);
                }}
              >
                {watch.enabled ? "Pause" : "Enable"}
              </button>
              <button
                className="ghost danger"
                onClick={(e) => {
                  e.stopPropagation();
                  void remove(watch);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="stack">
        <div className="card">
          <h2 style={{ fontSize: 15, marginBottom: 12 }}>
            {selected ? `Editing ${selected.name}` : "New watch"}
          </h2>
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {notice && (
            <div className="card" style={{ marginBottom: 12, borderColor: "var(--accent)" }}>
              <span className="faint">{notice}</span>
            </div>
          )}

          <div className="field">
            <label htmlFor="w-name">Name</label>
            <input
              id="w-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Faulty Xbox Series"
            />
          </div>

          <div className="field">
            <label htmlFor="w-criteria">Criteria (plain English)</label>
            <textarea
              id="w-criteria"
              value={draft.criteria}
              onChange={(e) => setDraft({ ...draft, criteria: e.target.value })}
              placeholder="Faulty, broken, untested or for-parts console located in Australia, including listings that describe a fault without using the word faulty."
            />
            <div className="hint">
              The only domain knowledge in the system. Describe the substance, not the seller's
              vocabulary.
            </div>
          </div>

          <div className="field">
            <label htmlFor="w-schema">Extract schema (optional JSON)</label>
            <textarea
              id="w-schema"
              className="mono"
              value={schemaText}
              onChange={(e) => {
                setSchemaText(e.target.value);
                setSchemaError(null);
              }}
              placeholder={'{"fault_type": {"enum": ["no_power","no_display","unknown"]}}'}
            />
            {schemaError && <div className="hint" style={{ color: "var(--risk)" }}>{schemaError}</div>}
            <div className="hint">
              Property name to JSON Schema fragment. Results land in the verdict's attributes; give
              an enum an "unknown" member so the model can say so.
            </div>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="w-lane">Lane</label>
              <select
                id="w-lane"
                value={draft.lane_id ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, lane_id: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">All lanes</option>
                {lanes.map((lane) => (
                  <option key={lane.id} value={lane.id}>
                    {lane.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="w-conf">Min confidence</label>
              <input
                id="w-conf"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={draft.min_confidence}
                onChange={(e) => setDraft({ ...draft, min_confidence: Number(e.target.value) })}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="w-max">Max price</label>
              <input
                id="w-max"
                type="number"
                min={0}
                value={draft.max_price ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, max_price: e.target.value ? Number(e.target.value) : null })
                }
                placeholder="none"
              />
            </div>
          </div>

          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="w-priority">Push priority</label>
              <select
                id="w-priority"
                value={draft.notify_priority}
                onChange={(e) =>
                  setDraft({ ...draft, notify_priority: e.target.value as Draft["notify_priority"] })
                }
              >
                <option value="low">low</option>
                <option value="normal">normal</option>
                <option value="high">high</option>
              </select>
            </div>
            <label className="row" style={{ gap: 6, marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={draft.notify}
                onChange={(e) => setDraft({ ...draft, notify: e.target.checked })}
              />
              Push
            </label>
            <label className="row" style={{ gap: 6, marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />
              Enabled
            </label>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="primary"
              disabled={!draft.name || !draft.criteria || saving}
              onClick={() => void save()}
            >
              {saving ? "Saving..." : selected ? "Save changes" : "Create watch"}
            </button>
            {selected && (
              <button className="ghost" onClick={() => edit(null)}>
                Cancel
              </button>
            )}
          </div>

          {selected && (
            <div className="stack" style={{ gap: 8, marginTop: 16, borderTop: "1px solid var(--rule)", paddingTop: 14 }}>
              <strong style={{ fontSize: 13 }}>Re-rate history</strong>
              <span className="faint">
                Apply the saved criteria to listings already collected. /test shows what a
                change would do; this makes it stick.
              </span>
              <div className="row">
                <button className="primary" onClick={() => void rescore(selected, "all", false)}>
                  Re-rate everything
                </button>
                <button onClick={() => void rescore(selected, "all", true)}>
                  ...and re-run stage 0
                </button>
                <button className="ghost" onClick={() => void rescore(selected, "rejects", false)}>
                  Rejections only
                </button>
              </div>
              <span className="faint">
                Re-rating clears the verdicts it covers, so those items leave the feed at once
                and come back only if the model confirms them again. Nothing pushes twice.
              </span>
            </div>
          )}
        </div>

        {selected ? (
          <TestPanel
            watch={selected}
            draftCriteria={draft.criteria}
            draftSchemaText={schemaText}
            draftMinConfidence={draft.min_confidence}
            draftMaxPrice={draft.max_price}
            lanes={lanes}
          />
        ) : (
          <div className="empty">
            Save a watch to dry-run it against the last seven days of stored listings.
          </div>
        )}
      </div>
    </div>
  );
}
